import React, { useEffect } from 'react';
import { Truck, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { LiveRatesBadge } from './LiveRatesBadge';

/**
 * Reusable live Freightcom rate selector. Shows the top 3 cheapest carrier
 * options for the given shipment, lets the user pick one, and (optionally)
 * returns the selection upward via `onSelect`.
 *
 * Parent owns the rates state via the `useShippingRates` hook; this is a
 * pure presentational component + selection handler.
 */
export const ShippingRateSelector = ({
  rates = [],
  loading = false,
  error = null,
  mockMode = false,
  selectedServiceCode = null,
  onSelect,
  pkg = null,
  // Total carriers (full set, not trimmed) — used by the trust badge
  // above the radio list. Falls back to `rates.length` when the parent
  // hasn't wired the new useShippingRates field yet.
  totalCarriers = null,
  compact = false,
}) => {
  // Auto-select cheapest on first load if caller hasn't picked one yet
  useEffect(() => {
    if (!selectedServiceCode && rates.length > 0 && onSelect) {
      onSelect(rates[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates]);

  if (loading) {
    return (
      <div
        className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200"
        data-testid="shipping-rates-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin text-[#4A7C59]" />
        <span className="text-sm text-gray-600">Fetching live shipping rates from Freightcom…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
        data-testid="shipping-rates-error"
      >
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Unable to retrieve shipping rates</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!rates.length) {
    // iter114s.94 — Previously this returned `null` which left the
    // customer staring at empty whitespace. Surface a friendly hint
    // so they understand the system isn't broken; it just needs a
    // moment (or the address isn't recognised by the carriers).
    return (
      <div
        className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800"
        data-testid="shipping-rates-empty"
      >
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">No live shipping rates yet</p>
          <p>
            Double-check the address above — most carriers need a complete
            postal/zip code, city, and state/province to quote. We&apos;ll
            re-check automatically as you type.
          </p>
        </div>
      </div>
    );
  }

  const badgeCount =
    totalCarriers != null && totalCarriers > 0 ? totalCarriers : rates.length;

  return (
    <div className="space-y-3" data-testid="shipping-rate-selector">
      {/* Trust signal: "Live shipping from N carriers" with Freightcom
          branding. Replaces the old amber-only mockMode banner — we
          fold the "preview pricing" copy into the badge itself when
          mockMode is true so the visual hierarchy stays consistent. */}
      <LiveRatesBadge count={badgeCount} mockMode={mockMode} />

      {pkg && !compact && (
        <p className="text-xs text-gray-500" data-testid="shipping-package-summary">
          Estimated package: {pkg.length_cm}×{pkg.width_cm}×{pkg.height_cm} cm · {pkg.weight_kg.toFixed(2)} kg
          {pkg.profile && <span className="ml-1 text-gray-400">({pkg.profile})</span>}
        </p>
      )}

      <div className="grid gap-2">
        {rates.map((r) => {
          const selected = r.service_code === selectedServiceCode;
          return (
            <button
              type="button"
              key={r.service_code}
              onClick={() => onSelect?.(r)}
              data-testid={`shipping-rate-option-${r.service_code}`}
              className={`text-left p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
                selected
                  ? 'border-[#4A7C59] bg-green-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-[#6FBF73]/60'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  selected ? 'bg-[#4A7C59] text-white' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-gray-900 truncate">
                    {r.carrier_name} <span className="text-gray-500 font-normal">· {r.service_level}</span>
                  </p>
                  <p className="text-lg font-bold text-[#2F5D3A] whitespace-nowrap">
                    ${r.total_price.toFixed(2)} <span className="text-xs font-normal text-gray-500">CAD</span>
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                  {r.estimated_delivery_days != null && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {r.estimated_delivery_days} business day{r.estimated_delivery_days === 1 ? '' : 's'}
                    </span>
                  )}
                  {r.note && <span className="text-amber-700">{r.note}</span>}
                </div>
              </div>
              <input
                type="radio"
                checked={selected}
                readOnly
                className="shrink-0 w-4 h-4 text-[#4A7C59] accent-[#4A7C59]"
                tabIndex={-1}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};
